/* eslint-disable */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton, Tooltip } from '@mui/material';
import { ContentCopy, DeleteOutline } from '@mui/icons-material';
import ConfirmationModal from '@/Components/Modals/MultiPoModal/ConfirmationModal';
import appVersionsServices from '@/Services/app-versions.services';
import notificationServices from '@/Services/notification.services';
import styles from './AppVersionsPage.module.scss';

// Upload app builds to s3://resoeepublic/UY-App/ and choose which build each customer gets.
// Works across all customers (the domain's clientId is ignored by the API); internal users only.

const GLOBAL_KEY = 'global';
const VERSION_PATTERN = /^\d+(\.\d+){1,3}$/;
const RELEASE_NOTES_MAX_CHARS = 20000;

function versionParts(version) {
	return (version || '').match(/\d+/g)?.map(Number) || [];
}

// Newest first, numeric per part so 1.14.0 sorts above 1.9.0
function sortVersions(list) {
	return [...list].sort((a, b) => {
		const pa = versionParts(a.version);
		const pb = versionParts(b.version);
		for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
			const diff = (pb[i] || 0) - (pa[i] || 0);
			if (diff) return diff;
		}
		return 0;
	});
}

// The Assigned to cell shows this many names; the rest are in its tooltip
const MAX_ASSIGNEES_SHOWN = 3;

function toRow(key, assignment, extra) {
	const saved = {
		appVersionId: assignment?.appVersionId ?? null,
		forceUpdate: !!assignment?.forceUpdate,
	};
	return { key, saved, draft: saved, ...extra };
}

export default function AppVersionsPage() {
	const userPermissions = useSelector((state) => state?.navigation?.userPermissions);

	const [versions, setVersions] = useState([]);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [search, setSearch] = useState('');
	const [savingKey, setSavingKey] = useState(null);
	const [deletingId, setDeletingId] = useState(null);
	const [pendingRemoval, setPendingRemoval] = useState(null);

	const [version, setVersion] = useState('');
	const [file, setFile] = useState(null);
	const [fileInputKey, setFileInputKey] = useState(0);
	const [notesMode, setNotesMode] = useState('text');
	const [releaseNotes, setReleaseNotes] = useState('');
	const [releaseNotesLink, setReleaseNotesLink] = useState('');
	const [progress, setProgress] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		setLoadFailed(false);
		try {
			const res = await appVersionsServices.listAppVersions();
			const data = res?.data || {};
			setVersions(sortVersions(data.versions || []));
			setRows([
				toRow(GLOBAL_KEY, data.globalDefault, {
					customerId: null, name: 'Global default', isGlobal: true, storeOverrides: 0,
				}),
				...(data.customers || []).map((c) => toRow(String(c.customerId), c, {
					customerId: c.customerId, name: c.name, storeOverrides: c.storeOverrides,
				})),
			]);
		} catch (e) {
			setLoadFailed(true);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (userPermissions?.INTERNAL) load();
	}, [userPermissions?.INTERNAL, load]);

	const versionById = useMemo(
		() => Object.fromEntries(versions.map((v) => [v.id, v])),
		[versions]
	);
	const globalRow = rows.find((r) => r.isGlobal);
	const globalVersion = versionById[globalRow?.saved.appVersionId]?.version;

	const visibleRows = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return rows;
		return rows.filter((r) => r.isGlobal
			|| String(r.customerId).includes(term)
			|| (r.name || '').toLowerCase().includes(term));
	}, [rows, search]);

	const updateDraft = (key, patch) => {
		setRows((prev) => prev.map((r) => (r.key === key ? { ...r, draft: { ...r.draft, ...patch } } : r)));
	};
	const resetDraft = (key) => {
		setRows((prev) => prev.map((r) => (r.key === key ? { ...r, draft: r.saved } : r)));
	};
	const isDirty = (r) => r.draft.appVersionId !== r.saved.appVersionId
		|| r.draft.forceUpdate !== r.saved.forceUpdate;

	const saveRow = async (row) => {
		setSavingKey(row.key);
		try {
			const res = await appVersionsServices.assignAppVersion({
				customerId: row.customerId,
				appVersionId: row.draft.appVersionId,
				forceUpdate: row.draft.forceUpdate,
			});
			if (res?.data) {
				const saved = { appVersionId: res.data.appVersionId, forceUpdate: !!res.data.forceUpdate };
				setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, saved, draft: saved } : r)));
				notificationServices.generateNotification({ type: 'success', message: `Saved for ${row.name}` });
			}
		} catch (e) {
			// handleResponse has already shown the error
		} finally {
			setSavingKey(null);
		}
	};

	// Names on each version from saved (not draft) rows, so the list follows saves on this page.
	// Rows start with the global default, so it is listed first.
	const assigneesByVersion = useMemo(() => {
		const names = {};
		rows.forEach((r) => {
			const id = r.saved.appVersionId;
			if (id != null) (names[id] = names[id] || []).push(r.name);
		});
		return names;
	}, [rows]);

	// Runs from the confirmation modal opened by the delete icon
	const confirmRemove = async () => {
		const v = pendingRemoval;
		if (!v) return;
		setDeletingId(v.id);
		try {
			const res = await appVersionsServices.deleteAppVersion({ appVersionId: v.id });
			if (res?.data) {
				setVersions((prev) => prev.filter((x) => x.id !== v.id));
				// Unsaved picks of the removed version go back to the saved value
				setRows((prev) => prev.map((r) => (r.draft.appVersionId === v.id ? { ...r, draft: r.saved } : r)));
				notificationServices.generateNotification({ type: 'success', message: res.message || `Version ${v.version} removed` });
			}
		} catch (e) {
			// handleResponse has already shown the error (e.g. still assigned to a hidden customer)
		} finally {
			setDeletingId(null);
			setPendingRemoval(null);
		}
	};

	const copyLink = async (link) => {
		try {
			await navigator.clipboard.writeText(link);
			notificationServices.generateNotification({ type: 'success', message: 'Download link copied' });
		} catch (e) {
			// Clipboard API needs https and permission; fall back to a selectable prompt
			window.prompt('Copy the download link', link);
		}
	};

	const onFileChange = (e) => {
		const picked = e.target.files?.[0] || null;
		setFile(picked);
		// Pre-fill the version from names like v1.14.25.apk or app-1.14.25.apk
		const fromName = picked?.name.match(/(\d+(?:\.\d+){1,3})/)?.[1];
		if (fromName && !version.trim()) setVersion(fromName);
	};

	const uploading = progress !== null;
	const cleanVersion = version.trim().replace(/^v/i, '');
	const notesValue = notesMode === 'text' ? releaseNotes.trim() : releaseNotesLink.trim();
	const canUpload = !!file && !!cleanVersion && !!notesValue && !uploading;

	const upload = async (e) => {
		e.preventDefault();
		if (!canUpload) return;
		if (!VERSION_PATTERN.test(cleanVersion)) {
			notificationServices.generateNotification({
				type: 'error', message: 'Enter the version as numbers separated by dots, e.g. 1.14.25',
			});
			return;
		}
		if (notesMode === 'link' && !/^https?:\/\//i.test(notesValue)) {
			notificationServices.generateNotification({
				type: 'error', message: 'The release notes link must start with http:// or https://',
			});
			return;
		}
		setProgress(0);
		try {
			const res = await appVersionsServices.uploadAppBuild({
				file,
				version: cleanVersion,
				releaseNotes: notesMode === 'text' ? notesValue : '',
				releaseNotesLink: notesMode === 'link' ? notesValue : '',
			}, setProgress);
			if (res?.data) {
				setVersions((prev) => sortVersions([res.data, ...prev]));
				setVersion('');
				setFile(null);
				setFileInputKey((k) => k + 1);
				setReleaseNotes('');
				setReleaseNotesLink('');
				notificationServices.generateNotification({
					type: 'success', message: res.message || `Version ${res.data.version} uploaded`,
				});
			}
		} catch (err) {
			// already notified by the service
		} finally {
			setProgress(null);
		}
	};

	if (!userPermissions?.INTERNAL) {
		return (
			<div className={styles.page}>
				<div className={styles.empty}>App Versions is available to internal users only.</div>
			</div>
		);
	}

	return (
		<div className={styles.page}>
			<header className={styles.header}>
				<h1>App Versions</h1>
				<p>Upload app builds and choose the version each customer is offered. Applies across all customers.</p>
			</header>

			<section className={styles.card}>
				<h2>Upload build</h2>
				<form className={styles.uploadForm} onSubmit={upload}>
					<label className={styles.field}>
						<span>APK file</span>
						<input key={fileInputKey} type='file' accept='.apk' onChange={onFileChange} disabled={uploading} />
					</label>
					<label className={styles.field}>
						<span>Version</span>
						<input
							type='text'
							placeholder='1.14.25'
							value={version}
							onChange={(e) => setVersion(e.target.value)}
							disabled={uploading}
						/>
						<small>
							{cleanVersion
								? `Uploads to s3://resoeepublic/UY-App/v${cleanVersion}.apk (public)`
								: 'Numbers separated by dots'}
						</small>
					</label>

					<div className={`${styles.field} ${styles.wide}`}>
						<span>Release notes</span>
						<div className={styles.tabs}>
							<button
								type='button'
								className={`${styles.tab} ${notesMode === 'text' ? styles.tabActive : ''}`}
								onClick={() => setNotesMode('text')}
								disabled={uploading}
							>
								Write notes
							</button>
							<button
								type='button'
								className={`${styles.tab} ${notesMode === 'link' ? styles.tabActive : ''}`}
								onClick={() => setNotesMode('link')}
								disabled={uploading}
							>
								Paste a link
							</button>
						</div>
						{notesMode === 'text' ? (
							<>
								<textarea
									rows={5}
									maxLength={RELEASE_NOTES_MAX_CHARS}
									placeholder={'- Faster GRN scanning\n- Fixed login on Android 14'}
									value={releaseNotes}
									onChange={(e) => setReleaseNotes(e.target.value)}
									disabled={uploading}
								/>
								<small>Required. Published as a public page next to the build; the app opens it as the release notes link.</small>
							</>
						) : (
							<>
								<input
									type='url'
									placeholder='https://...'
									value={releaseNotesLink}
									onChange={(e) => setReleaseNotesLink(e.target.value)}
									disabled={uploading}
								/>
								<small>Required. Must start with http:// or https://</small>
							</>
						)}
					</div>

					<div className={`${styles.actions} ${styles.wide}`}>
						<button type='submit' className={styles.primary} disabled={!canUpload}>
							{uploading ? 'Uploading...' : 'Upload'}
						</button>
						{uploading && (
							<div className={styles.progress}>
								<div className={styles.progressBar} style={{ width: `${progress}%` }} />
								<span>{progress < 100 ? `${progress}%` : 'Saving to S3...'}</span>
							</div>
						)}
					</div>
				</form>
			</section>

			<section className={styles.card}>
				<h2>Releases</h2>
				{versions.length === 0 ? (
					<div className={styles.empty}>{loading ? 'Loading...' : 'No builds uploaded yet.'}</div>
				) : (
					<div className={styles.tableWrap}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>Version</th>
									<th>Uploaded</th>
									<th>Download link</th>
									<th>Release notes</th>
									<th>Assigned to</th>
									<th aria-label='Actions' />
								</tr>
							</thead>
							<tbody>
								{versions.map((v) => {
									const assignees = [...(assigneesByVersion[v.id] || []), ...(v.assignedStores || [])];
									const hiddenCount = assignees.length - MAX_ASSIGNEES_SHOWN;
									return (
										<tr key={v.id}>
											<td className={styles.mono}>{v.version}</td>
											<td>{v.created_at || '-'}</td>
											<td>
												{v.link ? (
													<div className={styles.linkCell}>
														<a href={v.link} target='_blank' rel='noreferrer' className={styles.url}>{v.link}</a>
														<Tooltip title='Copy link'>
															<IconButton
																size='small'
																aria-label={`Copy download link for ${v.version}`}
																onClick={() => copyLink(v.link)}
															>
																<ContentCopy fontSize='small' />
															</IconButton>
														</Tooltip>
													</div>
												) : '-'}
											</td>
											<td>{v.releaseNotesLink ? <a href={v.releaseNotesLink} target='_blank' rel='noreferrer'>View</a> : '-'}</td>
											<td>
												{assignees.length ? (
													<Tooltip title={assignees.join(', ')} disableHoverListener={hiddenCount <= 0}>
														<span>
															{assignees.slice(0, MAX_ASSIGNEES_SHOWN).join(', ')}
															{hiddenCount > 0 && <span className={styles.more}>{` +${hiddenCount} more`}</span>}
														</span>
													</Tooltip>
												) : '-'}
											</td>
											<td>
												<Tooltip title={assignees.length ? 'Assigned - move these to another version before removing it' : 'Remove version'}>
													{/* span keeps the tooltip working while the button is disabled */}
													<span>
														<IconButton
															size='small'
															color='error'
															aria-label={`Remove version ${v.version}`}
															onClick={() => setPendingRemoval(v)}
															disabled={assignees.length > 0 || deletingId != null}
														>
															<DeleteOutline fontSize='small' />
														</IconButton>
													</span>
												</Tooltip>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<section className={styles.card}>
				<div className={styles.cardHead}>
					<h2>Customers</h2>
					<input
						className={styles.search}
						type='search'
						placeholder='Search by name or ID'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
				{loadFailed && (
					<div className={styles.empty}>
						Couldn't load app versions. <button type='button' className={styles.link} onClick={load}>Retry</button>
					</div>
				)}
				{loading && !loadFailed && rows.length === 0 && <div className={styles.empty}>Loading...</div>}
				{rows.length > 0 && (
					<div className={styles.tableWrap}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>ID</th>
									<th>Customer</th>
									<th>App version</th>
									<th>Force update</th>
									<th aria-label='Actions' />
								</tr>
							</thead>
							<tbody>
								{visibleRows.map((r) => {
									const dirty = isDirty(r);
									const saving = savingKey === r.key;
									const unassignedLabel = r.isGlobal
										? 'Not set'
										: `Global default${globalVersion ? ` (${globalVersion})` : ''}`;
									return (
										<tr key={r.key} className={r.isGlobal ? styles.globalRow : ''}>
											<td className={styles.mono}>{r.isGlobal ? '-' : r.customerId}</td>
											<td>
												<div className={styles.name}>{r.name}</div>
												{r.isGlobal && <small>Used by customers without their own version</small>}
												{r.storeOverrides > 0 && (
													<small>{`${r.storeOverrides} store override${r.storeOverrides > 1 ? 's' : ''} (not changed here)`}</small>
												)}
											</td>
											<td>
												<select
													value={r.draft.appVersionId ?? ''}
													onChange={(e) => updateDraft(r.key, { appVersionId: Number(e.target.value) })}
													disabled={saving}
												>
													{r.saved.appVersionId == null && <option value='' disabled>{unassignedLabel}</option>}
													{versions.map((v) => (
														<option key={v.id} value={v.id}>{v.version}</option>
													))}
												</select>
											</td>
											<td>
												<input
													type='checkbox'
													aria-label={`Force update for ${r.name}`}
													checked={r.draft.forceUpdate}
													onChange={(e) => updateDraft(r.key, { forceUpdate: e.target.checked })}
													disabled={saving || r.draft.appVersionId == null}
												/>
											</td>
											<td className={styles.rowActions}>
												{dirty && (
													<>
														<button
															type='button'
															className={styles.primary}
															onClick={() => saveRow(r)}
															disabled={saving || r.draft.appVersionId == null}
														>
															{saving ? 'Saving...' : 'Save'}
														</button>
														<button type='button' className={styles.link} onClick={() => resetDraft(r.key)} disabled={saving}>
															Undo
														</button>
													</>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<ConfirmationModal
				isOpen={!!pendingRemoval}
				heading={`Remove version ${pendingRemoval?.version ?? ''}?`}
				message="It will be hidden from this page and can't be assigned to customers. The build stays in S3, so download links already shared keep working."
				isLoading={deletingId != null}
				onClose={() => { if (deletingId == null) setPendingRemoval(null); }}
				onConfirm={confirmRemove}
			/>
		</div>
	);
}
